import React, { Suspense, useEffect, useRef } from 'react';

import './css/base.css';
import './css/theme.css';
import './css/layout.css';
import './css/global.css';
import './css/staticDefinition.css';
import './css/component.css';

import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Spinner } from '@maxis/components';

import ProtectedLayout from './Layout/ProtedtedLayout';
import { useBoundStore } from '@maxis/client-logic/store/useBoundStore';
import loadable from '@loadable/component';
import Layout from './Layout/Layout';
import { LOCAL_STORAGE_KEYS, PAGE_RENDER_TYPE } from '@maxis/client-logic/constants/enumerates';
import { componentLoader } from '@maxis/client-logic/utils/componentUtil';
import { withStaleChunkReload } from '@maxis/client-logic/utils/staleChunkReload';
import PageContainer from './Layout/PageContainer';
import TabContainer from './Layout/TabContainer';
import { CREATE_TAB_LIST, THEME } from '@maxis/client-logic/constants/common';
import useTabMenu from '@maxis/client-logic/hooks/useTabMenu';
import { toPayload } from '@maxis/client-logic/utils/querystringUtil';
import SystemBoard from './pages/System/SystemBoard';
import PasswordChange from './pages/PasswordChange';
import InitSetting from './pages/InitSetting';
import { getLocalStorageItem } from '@maxis/client-logic/utils/localStorageUtil';
import useAuth from '@maxis/client-logic/hooks/useAuth';
const Home = loadable(withStaleChunkReload(() => import('./pages/Home')));
const Login = loadable(withStaleChunkReload(() => import('./pages/Login')));
const OpenSourceLicenses = loadable(withStaleChunkReload(() => import('./pages/OpenSourceLicenses')));

const SYSTEM_LEGACY_ROUTE_REDIRECTS = [
    ['/system/sys100/sys110', '/system/sys110'],
    ['/system/sys100/sys140', '/system/sys140'],
    ['/system/sys200/sys220', '/system/sys220'],
    ['/system/sys200/sys230', '/system/sys230'],
    ['/system/sys200/sys250', '/system/sys250'],
];

const getSystemCanonicalPath = (pathname) => {
    const publicUrl = import.meta.env.VITE_PUBLIC_URL || '';
    const appPath =
        publicUrl && pathname.startsWith(publicUrl)
            ? pathname.slice(publicUrl.length) || '/'
            : pathname;
    const redirect = SYSTEM_LEGACY_ROUTE_REDIRECTS.find(([from]) => from === appPath);

    return redirect ? publicUrl + redirect[1] : null;
};

function App() {
    const { menuList, pageRenderType } = useBoundStore((state) => state.menu);
    const location = useLocation();
    const { createTabByNewId } = useTabMenu();
    const user = useBoundStore((state) => state.user);
    const openChk = useRef(false);
    const navigate = useNavigate();
    const { refresh } = useAuth();

    useEffect(() => {
        // Check if the location exists,
        // verify if the menuList has been rendered,
        // and ensure that this is excuted only once(openChk .,Ref)
        if (location?.pathname && location?.search && menuList.length && !openChk.current) {
            // the reason for using `openChk` is that when an item is added to `menuList` using `createTabByNewId`. useEffect is triggered.
            openChk.current = true;
            const searchObj = toPayload(location.search);

            // get the location path name and split it..
            const pageNameSplit = location?.pathname?.split('/');
            const pageName = pageNameSplit?.[pageNameSplit?.length - 1];

            // get the location information for create tab
            const key = searchObj?.key;

            // muse be excuted for the defined list only. it is `movePageName`
            // if there are other cases for moving the page, you need to add them to `CREATE_TAB_LIST`
            const movePageName = CREATE_TAB_LIST.find((item) => pageName?.includes(item));

            if (movePageName && key) {
                // create tab
                createTabByNewId([key], movePageName, searchObj);
            }
        }
    }, [menuList]);

    useEffect(() => {
        if (user?.propStr1) {
            document.body.classList = [user?.propStr1];
        } else {
            document.body.classList = [THEME.DEFAULT];
        }

        // 비밀번호 변경 한 적 업으면 비밀번호 변경 페이지로
        if (user?.pwChangeDt === null) {
            navigate(import.meta.env.VITE_PUBLIC_URL + '/password-change');
        }
    }, [user]);

    useEffect(() => {
        const accessToken = getLocalStorageItem(LOCAL_STORAGE_KEYS.ACCESS_TOKEN);
        if (accessToken) {
            refresh(location.pathname);
        }
    }, []);

    const systemCanonicalPath = getSystemCanonicalPath(location.pathname);

    if (systemCanonicalPath) {
        return <Navigate to={systemCanonicalPath + location.search} replace />;
    }

    return (
        // Routing 설정
        <Suspense fallback={<Spinner />}>
            <Routes>
                {/**
                 * 비밀번호 변경 페이지
                 * 최초 비밀번호 상태 일 경우 pwchangeDt 가 null 상태
                 *
                 * (로그인 아닌 상태 & 비밀 번호 변경을 한 경우) 일 경우 에는 Route 컴포넌트 자체를 렌더링 안함
                 */}
                {user?.pwChangeDt === null && (
                    <Route
                        path={import.meta.env.VITE_PUBLIC_URL + '/password-change'}
                        element={<PasswordChange />}
                    />
                )}
                {/**
                 * 플랫폼 초기 세팅 페이지
                 * initSetting이 "N"인 경우 (플랫폼 초기 세팅이 필요한 경우)에만 접근 가능
                 * 이미 초기 세팅이 완료된 경우 컴포넌트 내부에서 홈으로 리다이렉트
                 */}
                <Route
                    path={import.meta.env.VITE_PUBLIC_URL + '/init-setting'}
                    element={<InitSetting />}
                />
                <Route path={import.meta.env.VITE_PUBLIC_URL} element={<Layout />}>
                    <Route
                        path={import.meta.env.VITE_PUBLIC_URL + '/login'}
                        element={
                            <>
                                <Login />
                            </>
                        }
                    />
                    <Route
                        path={import.meta.env.VITE_PUBLIC_URL + '/licenses'}
                        element={
                            <ProtectedLayout>
                                <OpenSourceLicenses />
                            </ProtectedLayout>
                        }
                    />

                    {pageRenderType === PAGE_RENDER_TYPE.PAGE ? (
                        <Route
                            path={import.meta.env.VITE_PUBLIC_URL + '/'}
                            element={
                                <>
                                    <Home />
                                </>
                            }
                        />
                    ) : null}

                    {pageRenderType === PAGE_RENDER_TYPE.PAGE &&
                        menuList.map((menuItem) => {
                            if (menuItem.programPath) {
                                const Component = componentLoader(menuItem.programPath);
                                return (
                                    <Route
                                        key={menuItem.menuId}
                                        path={
                                            import.meta.env.VITE_PUBLIC_URL +
                                            menuItem.programRoutePath
                                        }
                                        element={
                                            // menuItem.sideYn === 'Y' ? (
                                            <ProtectedLayout>
                                                <PageContainer
                                                    menuName={menuItem.menuName}
                                                    menuId={menuItem.menuId}
                                                    helpYn={menuItem.helpYn}
                                                    menuNameListId={menuItem.menuNameListId}
                                                    menuNameList={menuItem.menuNameList}
                                                >
                                                    <Component />
                                                </PageContainer>
                                            </ProtectedLayout>
                                            // ) : (
                                            //   <Component />
                                            // )
                                        }
                                    />
                                );
                            }
                        })}
                    {pageRenderType === PAGE_RENDER_TYPE.TAB ? (
                        <>
                            <Route
                                path={import.meta.env.VITE_PUBLIC_URL + '/'}
                                element={<TabContainer />}
                            />
                            <Route
                                path={import.meta.env.VITE_PUBLIC_URL + '/*'}
                                element={<TabContainer />}
                            />
                        </>
                    ) : null}
                </Route>
            </Routes>
        </Suspense>
    );
}

export default App;
